/**
 * ตารางสิทธิ์ของแผนกเดียว — `GET`/`POST /v1/admin/permissions`
 *
 * สามสถานะที่ต้องแยกจากกันเสมอ: กำลังโหลด / โหลดสำเร็จ / โหลดไม่สำเร็จ ตารางว่าง
 * ที่ใช้แทนสองอย่างหลังคือหน้าจอที่โกหกผู้ดูแล — และที่นี่มันโกหกเรื่องที่แพงที่สุด
 * เรื่องหนึ่ง: "แผนกนี้ยังไม่ได้รับสิทธิ์อะไรเลย" กับ "อ่านไม่ได้ว่าแผนกนี้ได้อะไรไปบ้าง"
 * นำไปสู่การกดบันทึกคนละแบบ
 *
 * **กติกาของ D-060 อยู่ที่นี่**: แถวที่ `is_system_admin_module` เป็น `true` ถูกเรนเดอร์
 * ด้วยช่องติ๊กที่ **ปิดทั้งสี่ช่อง** ไม่มีปุ่มบันทึก และมีหมายเหตุบอกว่าสิทธิ์กลุ่มนั้น
 * มาจาก role `ADMIN` ไม่ใช่จากตารางนี้ ส่วนทางเขียนเดินผ่าน `toSaveBody()` ซึ่งคืน
 * `null` ให้แถวเหล่านั้นเสมอ — หน้าจอจึงประกอบ `POST` ของมันขึ้นมาไม่ได้เลย ไม่ใช่
 * แค่ "ไม่แสดงปุ่ม"
 *
 * ⚠️ ทั้งหมดนี้เป็น UX layer ไม่ใช่ด่านความปลอดภัย เซิร์ฟเวอร์ตอบ
 * `422 SYSTEM_ADMIN_MODULE_NOT_GRANTABLE` อยู่แล้วเป็นด่านจริง ที่ปิดช่องให้เพราะ
 * หน้าจอที่เปิดให้ติ๊กสิ่งที่ API ปฏิเสธเสมอ คือหน้าจอที่สัญญาในสิ่งที่ให้ไม่ได้
 */

import { useEffect, useState } from 'react';

import { RbacAdminFailureNotice } from './RbacAdminFailureNotice';
import {
  PERMISSION_FLAG_COLUMNS,
  draftFromMatrix,
  flagsEqual,
  flagsOf,
  isRowGrantable,
  toSaveBody,
  toggleFlag,
} from './permissionMatrixView';
import { StatusBadge } from '../StatusBadge';
import { InfoHint } from '../components/InfoHint';
import {
  fetchPermissionMatrix,
  setModulePermission,
  type RbacAdminFailure,
} from '../../lib/rbacAdmin/client';
import type {
  DepartmentPermissionMatrix,
  ModulePermissionRow,
  PermissionFlagKey,
  PermissionFlags,
} from '../../lib/rbacAdmin/types';

export interface DepartmentPermissionGridProps {
  readonly departmentId: string;
  readonly departmentName: string;
  readonly apiBaseUrl: string;
  readonly fetchImpl?: typeof fetch;
  /** มาจาก flag `can_edit` ของ `RBAC_ADMIN` ที่เซิร์ฟเวอร์ส่งมาเท่านั้น */
  readonly canEdit: boolean;
}

type MatrixState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly matrix: DepartmentPermissionMatrix }
  | RbacAdminFailure;

interface SaveFailure {
  readonly moduleCode: string;
  readonly failure: RbacAdminFailure;
}

export function DepartmentPermissionGrid({
  departmentId,
  departmentName,
  apiBaseUrl,
  fetchImpl,
  canEdit,
}: DepartmentPermissionGridProps) {
  const [state, setState] = useState<MatrixState>({ status: 'loading' });
  /** ค่าที่ผู้ดูแลกำลังแก้อยู่ ตั้งต้นจากค่าที่เซิร์ฟเวอร์ส่งมา ไม่ใช่ค่าที่หน้าจอเดา */
  const [draft, setDraft] = useState<Readonly<Record<string, PermissionFlags>>>({});
  const [savingModule, setSavingModule] = useState<string | null>(null);
  const [savedModule, setSavedModule] = useState<string | null>(null);
  const [saveFailure, setSaveFailure] = useState<SaveFailure | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    setState({ status: 'loading' });
    setDraft({});
    setSavingModule(null);
    setSavedModule(null);
    setSaveFailure(null);

    void fetchPermissionMatrix(departmentId, {
      apiBaseUrl,
      fetchImpl,
      signal: controller.signal,
    }).then((result) => {
      if (!active || result.status === 'cancelled') {
        return;
      }
      if (result.status === 'ready') {
        setDraft(draftFromMatrix(result.matrix.modules));
      }
      setState(result.status === 'ready' ? { status: 'ready', matrix: result.matrix } : result);
    });

    return () => {
      active = false;
      controller.abort();
    };
  }, [departmentId, apiBaseUrl, fetchImpl, reloadToken]);

  const toggle = (row: ModulePermissionRow, key: PermissionFlagKey) => {
    // ด่านเดียวกับตอนบันทึก: แถวกลุ่ม System Administration ไม่มีสถานะร่างให้แก้เลย
    if (!isRowGrantable(row)) {
      return;
    }
    setSavedModule(null);
    setDraft((previous) => {
      const current = previous[row.module_code];
      if (current === undefined) {
        return previous;
      }
      return { ...previous, [row.module_code]: toggleFlag(current, key) };
    });
  };

  const saveRow = async (row: ModulePermissionRow) => {
    const flags = draft[row.module_code];
    if (flags === undefined || savingModule !== null) {
      return;
    }
    // **จุดที่ D-060 ถูกบังคับในโค้ด** — `toSaveBody()` คืน `null` ให้แถวกลุ่ม
    // System Administration ไม่มี branch ใดในไฟล์นี้ที่ประกอบ body ขึ้นมาเองได้
    const body = toSaveBody(departmentId, row, flags);
    if (body === null) {
      return;
    }

    setSavingModule(row.module_code);
    setSavedModule(null);
    setSaveFailure(null);

    const result = await setModulePermission(body, { apiBaseUrl, fetchImpl });
    setSavingModule(null);
    if (result.status === 'cancelled') {
      return;
    }
    if (result.status !== 'ready') {
      setSaveFailure({ moduleCode: row.module_code, failure: result });
      return;
    }

    // เขียนกลับด้วย **แถวที่เซิร์ฟเวอร์บอกว่าเก็บจริง** ไม่ใช่ค่าที่เราเพิ่งส่งไป —
    // สองอย่างนี้ต่างกันได้ และหน้าจอที่แสดงค่าที่ตัวเองส่งไปคือหน้าจอที่ยืนยันตัวเอง
    const stored = flagsOf(result.stored);
    setState((previous) =>
      previous.status === 'ready'
        ? {
            status: 'ready',
            matrix: {
              ...previous.matrix,
              modules: previous.matrix.modules.map((entry) =>
                entry.module_code === result.stored.module_code ? { ...entry, ...stored } : entry,
              ),
            },
          }
        : previous,
    );
    setDraft((previous) => ({ ...previous, [result.stored.module_code]: stored }));
    setSavedModule(result.stored.module_code);
  };

  if (state.status === 'loading') {
    return (
      <div className="cap-notice" data-testid="rbac-matrix-loading" role="status">
        <p className="cap-notice__text">กำลังโหลดตารางสิทธิ์ของ {departmentName}…</p>
      </div>
    );
  }

  if (state.status !== 'ready') {
    return (
      <RbacAdminFailureNotice
        failure={state}
        context="matrix"
        departmentName={departmentName}
        onRetry={() => {
          setReloadToken((token) => token + 1);
        }}
      />
    );
  }

  return (
    <section className="cap-card cap-card--table" aria-label={`ตารางสิทธิ์ของ ${departmentName}`}>
      <div className="cap-card__head">
        <h2 className="cap-card__title" data-testid="rbac-matrix-department">
          {state.matrix.department_name}{' '}
          <InfoHint
            label="เกี่ยวกับตารางสิทธิ์นี้"
            text={`แสดงโมดูลทั้งหมด ${state.matrix.modules.length} รายการในแคตาล็อก — โมดูลที่ยังไม่เคยตั้งค่าจะไม่ติ๊กไว้ ไม่ใช่ถูกซ่อน`}
          />
        </h2>
      </div>
      <div className="cap-table-wrap">
        <table className="cap-table" data-testid="rbac-matrix-table">
          <caption className="cap-visually-hidden">
            ตารางสิทธิ์ของแผนก {state.matrix.department_name} แจกแจงทุกโมดูลในแคตาล็อก
            พร้อมสี่สิทธิ์ที่เป็นอิสระต่อกัน — โมดูลกลุ่ม System Administration ติ๊กไม่ได้
            เพราะสิทธิ์ของมันมาจาก role ADMIN เท่านั้น (D-060)
          </caption>
          <thead>
            <tr>
              <th scope="col">โมดูล</th>
              {PERMISSION_FLAG_COLUMNS.map((column) => (
                <th key={column.key} scope="col">
                  {column.label}
                </th>
              ))}
              <th scope="col">บันทึก</th>
            </tr>
          </thead>
          <tbody>
            {state.matrix.modules.map((row) => {
              const grantable = isRowGrantable(row);
              const flags = draft[row.module_code] ?? flagsOf(row);
              const saving = savingModule === row.module_code;
              const dirty = !flagsEqual(flags, flagsOf(row));

              return (
                <tr
                  key={row.module_code}
                  data-testid={`rbac-matrix-row-${row.module_code}`}
                  data-system-admin-module={String(row.is_system_admin_module)}
                >
                  <th scope="row" className="cap-table__row-head">
                    {row.module_name}
                    <br />
                    <code className="cap-table__code">{row.module_code}</code>
                  </th>
                  {PERMISSION_FLAG_COLUMNS.map((column) => (
                    <td key={column.key}>
                      <input
                        type="checkbox"
                        data-testid={`rbac-flag-${row.module_code}-${column.key}`}
                        aria-label={`${row.module_name} — ${column.label}`}
                        checked={flags[column.key]}
                        // ปิดด้วยสองเหตุผลที่ต่างกันสิ้นเชิง: ไม่มีสิทธิ์ EDIT (UX)
                        // หรือเป็นโมดูลกลุ่มระบบที่มอบผ่านตารางนี้ไม่ได้เลย (D-060)
                        disabled={!canEdit || !grantable || saving}
                        onChange={() => {
                          toggle(row, column.key);
                        }}
                      />
                    </td>
                  ))}
                  <td>
                    {!grantable ? (
                      <span
                        className="cap-field__hint"
                        data-testid={`rbac-system-admin-note-${row.module_code}`}
                      >
                        กลุ่ม System Administration — สิทธิ์มาจาก role ADMIN เท่านั้น
                        ตารางนี้มอบให้ไม่ได้ (D-060)
                      </span>
                    ) : canEdit ? (
                      <button
                        type="button"
                        className="cap-button cap-button--primary"
                        data-testid={`rbac-save-${row.module_code}`}
                        disabled={!dirty || savingModule !== null}
                        onClick={() => {
                          void saveRow(row);
                        }}
                      >
                        {saving ? 'กำลังบันทึก…' : 'บันทึกแถวนี้'}
                      </button>
                    ) : (
                      <span className="cap-field__hint">ดูได้อย่างเดียว</span>
                    )}
                    {savedModule === row.module_code ? (
                      <StatusBadge tone="success">บันทึกแล้ว</StatusBadge>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {saveFailure === null ? null : (
        <div className="cap-card__foot">
          <RbacAdminFailureNotice
            failure={saveFailure.failure}
            context="save"
            departmentName={departmentName}
          />
        </div>
      )}
    </section>
  );
}
